import { type RefObject, useEffect } from 'react'

import { useTheme } from 'styled-components'
import { useMediaQuery } from 'usehooks-ts'

import { PROMO_PROXIMITY_PROPERTY, PROMO_TINT } from '../styles/promoTintStyles'

const PROMO_ROW_SELECTOR = '[data-promo]'

interface Point {
  x: number
  y: number
}

type Rect = Pick<DOMRect, 'left' | 'right' | 'top' | 'bottom'>

export const getDistanceToRect = ({ x, y }: Point, rect: Rect): number => {
  const dx = Math.max(rect.left - x, 0, x - rect.right)
  const dy = Math.max(rect.top - y, 0, y - rect.bottom)
  return Math.hypot(dx, dy)
}

/** Maps pointer distance to a 0–1 proximity. `null` means the pointer is outside the sidenav. */
export const getPromoProximity = (distancePx: number | null): number => {
  if (distancePx === null) return 0
  const { fullDistancePx, noneDistancePx, proximitySteps } = PROMO_TINT
  if (distancePx <= fullDistancePx) return 1
  if (distancePx >= noneDistancePx) return 0
  const proximity =
    (noneDistancePx - distancePx) / (noneDistancePx - fullDistancePx)
  return Math.round(proximity * proximitySteps) / proximitySteps
}

const hasBox = (rect: Rect) => rect.right > rect.left && rect.bottom > rect.top

export const getRowProximity = (pointer: Point | null, rect: Rect): number => {
  if (!pointer || !hasBox(rect)) return 0
  return getPromoProximity(getDistanceToRect(pointer, rect))
}

export const updatePromoRows = (nav: HTMLElement, pointer: Point | null) => {
  const rows = Array.from(nav.querySelectorAll<HTMLElement>(PROMO_ROW_SELECTOR))
  const proximities = rows.map((row) =>
    pointer ? getRowProximity(pointer, row.getBoundingClientRect()) : 0
  )
  rows.forEach((row, index) => {
    const value = String(proximities[index])
    if (row.style.getPropertyValue(PROMO_PROXIMITY_PROPERTY) === value) return
    row.style.setProperty(PROMO_PROXIMITY_PROPERTY, value)
  })
}

export const usePromoProximity = (navRef: RefObject<HTMLElement>) => {
  const theme = useTheme()
  const canShowTint = useMediaQuery(
    `(width > ${theme.breakpoints.values.xl}px) and (any-hover: hover)`
  )

  useEffect(() => {
    const nav = navRef.current
    if (!nav || !canShowTint) return undefined

    let frame = 0
    let pointer: Point | null = null

    const flush = () => {
      frame = 0
      updatePromoRows(nav, pointer)
    }

    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(flush)
    }

    const handleMove = (event: PointerEvent) => {
      if (event.pointerType === 'touch') return
      pointer = { x: event.clientX, y: event.clientY }
      schedule()
    }

    const handleLeave = () => {
      pointer = null
      schedule()
    }

    nav.addEventListener('pointermove', handleMove)
    nav.addEventListener('pointerleave', handleLeave)
    nav.addEventListener('pointercancel', handleLeave)

    return () => {
      nav.removeEventListener('pointermove', handleMove)
      nav.removeEventListener('pointerleave', handleLeave)
      nav.removeEventListener('pointercancel', handleLeave)
      if (frame) cancelAnimationFrame(frame)
      updatePromoRows(nav, null)
    }
  }, [navRef, canShowTint])
}
